import { useState } from "react";
import { useNavigate } from "react-router-dom";

import type { ICreatePro014CampaignResponse, StoreSelectorValue } from "@/@types/campaign";
import type { CampaignConflictItem, ResolveConflictResult } from "@/@types/campaign-conflict";
import { toast } from "@/components/ui/use-toast";
import {
  useCreatePro014CampaignMutation,
  useResolveConflictItemMutation,
} from "@/services/campaign-pro014";

import {
  buildConflictResolutions,
  describePartialResolution,
  dropStagedCampaigns,
} from "../../utils/conflict-resolution";
import { TOAST_VARIANT } from "../../utils/messages";
import { conflictsFromError, describeApiError } from "../../utils/pro014-api-errors";
import { buildCreatePro014Payload } from "../../utils/pro014-payload";
import { normalizeStoreSelection } from "../../utils/pro014-stores-diff";
import { useAllStoreCodes } from "../../hooks/use-all-store-codes";
import {
  buildPro014EditPlan,
  hasPro014EditChanges,
  pro014StoreScope,
} from "../../utils/pro014-update-diff";
import {
  CAMPAIGNS_LIST_PATH,
  CONFLICT_FILTER,
  PRO014_WIZARD_MODE,
  PRO014_WIZARD_TEXTS,
  REDIRECT_DELAY_MS,
} from "../constants";
import type {
  Pro014CampaignData,
  Pro014EditSource,
  Pro014Submission,
  Pro014WizardMode,
  Pro014WizardState,
  QueuedCreation,
} from "../types";
import { usePro014ConflictPrevalidation } from "./use-pro014-conflict-prevalidation";
import { usePro014EditSubmit } from "./use-pro014-edit-submit";

const FAILURE_BY_MODE: Record<Pro014WizardMode, string> = {
  [PRO014_WIZARD_MODE.CREATE]: PRO014_WIZARD_TEXTS.CREATE_FAILED,
  [PRO014_WIZARD_MODE.EDIT]: PRO014_WIZARD_TEXTS.UPDATE_FAILED,
};

interface UsePro014SubmitParams {
  mode: Pro014WizardMode;
  editSource?: Pro014EditSource;
  wizard: Pro014WizardState;
  openConflictModal: (conflicts: CampaignConflictItem[], unresolvableItemCodes?: string[]) => void;
}

export function usePro014Submit({
  mode,
  editSource,
  wizard,
  openConflictModal,
}: UsePro014SubmitParams): Pro014Submission {
  const navigate = useNavigate();
  const [createCampaign] = useCreatePro014CampaignMutation();
  const [resolveConflictItem] = useResolveConflictItemMutation();
  const { prevalidate, validationProgress } = usePro014ConflictPrevalidation();
  const saveEdit: (data: Pro014CampaignData) => Promise<boolean> = usePro014EditSubmit(editSource);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [queuedCreation, setQueuedCreation] = useState<QueuedCreation | null>(null);

  const allStoreCodes: number[] = useAllStoreCodes();
  const scope: StoreSelectorValue = editSource
    ? pro014StoreScope(editSource.detail)
    : normalizeStoreSelection(wizard.data, allStoreCodes);

  const redirectToList = (): void => {
    setTimeout(() => navigate(CAMPAIGNS_LIST_PATH), REDIRECT_DELAY_MS);
  };

  const reportConflicts = (found: CampaignConflictItem[]): void => {
    wizard.setConflicts(found);
    wizard.setConflictFilter(CONFLICT_FILTER.CONFLICT);
    openConflictModal(found);
    toast({
      title: PRO014_WIZARD_TEXTS.CONFLICT_TITLE,
      description: PRO014_WIZARD_TEXTS.CONFLICT_DESCRIPTION(found.length),
      variant: TOAST_VARIANT.DESTRUCTIVE,
    });
  };

  const reportUnresolvable = (itemCodes: string[], message: string): void => {
    const failed: CampaignConflictItem[] = wizard.dropPendingRemovals(itemCodes);
    wizard.setConflicts(failed);
    wizard.setConflictFilter(CONFLICT_FILTER.CONFLICT);
    openConflictModal(failed, itemCodes);
    toast({
      title: PRO014_WIZARD_TEXTS.UNRESOLVABLE_TITLE,
      description: message || PRO014_WIZARD_TEXTS.UNRESOLVABLE_DESCRIPTION,
      variant: TOAST_VARIANT.DESTRUCTIVE,
    });
  };

  const create = async (): Promise<void> => {
    const itemCodes: Set<string> = new Set(wizard.data.items.map((item) => item.sku));
    const result: ICreatePro014CampaignResponse = await createCampaign(
      buildCreatePro014Payload(
        { ...wizard.data, ...scope },
        buildConflictResolutions(wizard.pendingRemovals, itemCodes),
      ),
    ).unwrap();
    if ("queued" in result.data) {
      setQueuedCreation({ jobId: result.data.jobId, itemsCount: result.data.itemsCount });
      return;
    }
    wizard.clearPendingRemovals();
    toast({
      title: PRO014_WIZARD_TEXTS.SUCCESS_TITLE,
      description: PRO014_WIZARD_TEXTS.CREATED(wizard.data.description, result.data.code),
      variant: TOAST_VARIANT.DEFAULT,
    });
    redirectToList();
  };

  const applyStagedRemovals = async (): Promise<ResolveConflictResult[]> => {
    const itemCodes: Set<string> = new Set(wizard.data.items.map((item) => item.sku));
    const results: ResolveConflictResult[] = [];
    for (const resolution of buildConflictResolutions(wizard.pendingRemovals, itemCodes)) {
      results.push(await resolveConflictItem(resolution).unwrap());
    }
    return results;
  };

  const edit = async (): Promise<void> => {
    const removals: ResolveConflictResult[] = await applyStagedRemovals();
    const failed: ResolveConflictResult[] = removals.filter((removal) => !removal.fullyResolved);
    if (failed.length > 0) {
      wizard.dropPendingRemovals(
        removals.filter((removal) => removal.fullyResolved).map((removal) => removal.itemCode),
      );
      reportUnresolvable(
        failed.map((removal) => removal.itemCode),
        describePartialResolution(failed[0]),
      );
      return;
    }
    wizard.clearPendingRemovals();
    if (
      editSource &&
      removals.length > 0 &&
      !hasPro014EditChanges(buildPro014EditPlan(editSource, wizard.data))
    ) {
      toast({
        title: PRO014_WIZARD_TEXTS.SUCCESS_TITLE,
        description: PRO014_WIZARD_TEXTS.REMOVED_FROM_PREVIOUS(removals.length),
        variant: TOAST_VARIANT.DEFAULT,
      });
      redirectToList();
      return;
    }
    const saved: boolean = await saveEdit(wizard.data);
    if (!saved || !editSource) return;
    toast({
      title: PRO014_WIZARD_TEXTS.SUCCESS_TITLE,
      description: PRO014_WIZARD_TEXTS.UPDATED(wizard.data.description, editSource.campaignCode),
      variant: TOAST_VARIANT.DEFAULT,
    });
    redirectToList();
  };

  const SUBMITTERS: Record<Pro014WizardMode, () => Promise<void>> = {
    [PRO014_WIZARD_MODE.CREATE]: create,
    [PRO014_WIZARD_MODE.EDIT]: edit,
  };

  const handleError = (error: unknown): void => {
    const found: CampaignConflictItem[] = conflictsFromError(error);
    if (found.length > 0) {
      reportConflicts(found);
      return;
    }
    toast({
      title: PRO014_WIZARD_TEXTS.ERROR_TITLE,
      description: describeApiError(error, FAILURE_BY_MODE[mode]),
      variant: TOAST_VARIANT.DESTRUCTIVE,
    });
  };

  const submit = async (): Promise<void> => {
    if (wizard.conflicts.length > 0) {
      openConflictModal(wizard.conflicts);
      return;
    }
    setIsSubmitting(true);
    try {
      const found: CampaignConflictItem[] = dropStagedCampaigns(
        await prevalidate({
          items: wizard.data.items,
          startDate: wizard.data.startDate,
          endDate: wizard.data.endDate,
          scope,
          gradeRestricted: wizard.data.gradeRestricted,
          excludeCampaignCode: editSource?.campaignCode,
        }),
        wizard.pendingRemovals,
      );
      if (found.length > 0) {
        reportConflicts(found);
        return;
      }
      await SUBMITTERS[mode]();
    } catch (error: unknown) {
      handleError(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return {
    submit,
    isSubmitting,
    validationProgress,
    queuedCreation,
    closeQueuedCreation: () => {
      setQueuedCreation(null);
      navigate(CAMPAIGNS_LIST_PATH);
    },
  };
}
